import React from 'react';
import {
  MapPin,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Clock,
  DollarSign,
  Navigation,
} from 'lucide-react';
import { formatLocationLabel } from '../../../services/vendorLocationService';

const AREA_TYPE_LABELS = {
  city: 'City',
  county: 'County',
  state: 'State',
  country: 'Country',
  custom: 'Custom',
};

const LocationCard = ({ location, onEdit, onDelete, onToggle, isToggling, isDeleting }) => {
  const geoLabel = formatLocationLabel(location);

  return (
    <div
      className={`bg-white rounded-xl border p-4 sm:p-5 transition-shadow hover:shadow-sm ${
        location.is_active ? 'border-gray-200' : 'border-gray-200 opacity-75 bg-gray-50/50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
              location.is_active ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'
            }`}
          >
            <MapPin className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-gray-900 truncate">{location.area_name}</h3>
              <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {AREA_TYPE_LABELS[location.area_type] || location.area_type}
              </span>
              {!location.is_active && (
                <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  Inactive
                </span>
              )}
            </div>
            {geoLabel && geoLabel !== location.area_name && (
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <Navigation className="w-3.5 h-3.5 flex-shrink-0" />
                {geoLabel}
              </p>
            )}
            {location.address_line && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{location.address_line}</p>
            )}
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-600">
              {Number(location.travel_fee) > 0 && (
                <span className="inline-flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5" />
                  Travel fee ${Number(location.travel_fee).toFixed(2)}
                </span>
              )}
              {Number(location.travel_time_minutes) > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  ~{location.travel_time_minutes} min travel
                </span>
              )}
            </div>
            {location.notes && (
              <p className="text-xs text-gray-500 mt-2 line-clamp-2 italic">{location.notes}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onToggle(location)}
            disabled={isToggling}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
            title={location.is_active ? 'Deactivate' : 'Activate'}
          >
            {location.is_active ? (
              <ToggleRight className="w-5 h-5 text-emerald-600" />
            ) : (
              <ToggleLeft className="w-5 h-5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onEdit(location)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(location)}
            disabled={isDeleting}
            className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationCard;
